/**
 * Read the OS location permission WITHOUT asking for it.
 *
 * Exists so the geofence can tell "this player changed something" from "this
 * player tabbed away and back". Both look identical to a foreground event, and
 * on the vendor path the difference is money: every re-check that reaches Radar
 * is a billed lookup, so a re-check fired on the second case buys an answer we
 * already have, as often as the player cares to switch apps.
 *
 * It also answers the question the login check should have been asking all
 * along. A denied permission means the vendor CANNOT produce a usable verdict,
 * so a lookup bought under it is guaranteed waste.
 *
 * NOTHING HERE PROMPTS. `checkPermissions` and `navigator.permissions.query`
 * both report current state only. That matters: a read that prompted would make
 * this unusable on resume, because it would throw a system dialog at a player
 * who merely switched back to the app.
 *
 * THIS IS NOT A SECURITY CONTROL AND MUST NOT BECOME ONE. It is read from a
 * page the player controls, so a modified client can claim anything. All it
 * decides is whether WE spend a lookup and which copy we show. Admission is
 * still decided server-side from a vendor-signed token, which this cannot
 * forge.
 */
import { Capacitor } from '@capacitor/core';

/**
 * `unknown` is a real and common answer, not an error case.
 *
 * `navigator.permissions` is absent in some WebViews, and the Permissions API
 * has never been required to support the `geolocation` name. Callers must treat
 * `unknown` as "proceed as you would have before this existed" rather than as
 * denied, because guessing denied would block a player whose permission is
 * perfectly fine on a browser that simply will not tell us.
 */
export type LocationPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

/**
 * Capacitor reports `prompt-with-rationale` on Android for the state after a
 * user has declined once but can still be asked again. It is a `prompt` for
 * every purpose here: the vendor call will raise a dialog and may well succeed,
 * so it must not be collapsed into `denied`.
 */
function normalizeNative(state: string | undefined): LocationPermission {
  if (state === 'granted') return 'granted';
  if (state === 'denied') return 'denied';
  if (state === 'prompt' || state === 'prompt-with-rationale') return 'prompt';
  return 'unknown';
}

export async function readLocationPermission(): Promise<LocationPermission> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { Geolocation } = await import('@capacitor/geolocation');
      const status = await Geolocation.checkPermissions();
      // EITHER FIELD COUNTS AS GRANTED, and the comment that used to sit here
      // said so while the code did the opposite -- it read `location` alone.
      //
      // Android reports `location: 'denied'` with `coarseLocation: 'granted'`
      // for a player who chose Approximate. Region gating runs on a coarse fix
      // anyway, so that player is granted for our purposes; reading only the
      // precise field called them denied, the resume re-check short-circuited,
      // and they stayed blocked until another login.
      //
      // `warmLocationProvider.ts` already accepts either, so this also stops
      // two parts of the same flow disagreeing about whether we have location.
      if (status?.location === 'granted' || status?.coarseLocation === 'granted') {
        return 'granted';
      }
      return normalizeNative(status?.location);
    }

    // Optional chaining on BOTH: the API is missing entirely in some WebViews,
    // and Safari historically threw on an unsupported permission name rather
    // than rejecting. The catch below covers the throw; this covers the absence.
    const status = await navigator.permissions?.query({
      name: 'geolocation' as PermissionName
    });
    if (status?.state === 'granted') return 'granted';
    if (status?.state === 'denied') return 'denied';
    if (status?.state === 'prompt') return 'prompt';
    return 'unknown';
  } catch {
    // Never throws. A caller deciding whether to spend a lookup must not have
    // to handle an exception from the thing that was supposed to save it one.
    return 'unknown';
  }
}
